import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import styles from "./FolderCard.module.scss";
import { ReactComponent as FolderSvg } from "../../images/folder.svg";
import { Checkbox } from "antd";

interface FolderCardProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  minHeight?: number;
  minWidth?: number;
  onClick?: () => void;
  folderType?: 'folder' | 'file';
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (checked: boolean) => void;
  sharedUsers?: Array<{
    id: number;
    firstName: string;
    lastName: string;
    thumbnailUrl?: string;
    organizationName?: string;
  }>;
}

const DEFAULT_WIDTH = 245;
const DEFAULT_HEIGHT = 353;

const MOBILE_WIDTH = 165;
const MOBILE_HEIGHT = 237; // aspect ratio: 165 / 237
const MOBILE_RATIO = MOBILE_HEIGHT / MOBILE_WIDTH;

const FolderCard: React.FC<FolderCardProps> = ({
  children,
  className,
  style,
  minHeight = DEFAULT_HEIGHT,
  minWidth = DEFAULT_WIDTH,
  onClick,
  folderType = 'folder',
  selectable = false,
  selected = false,
  onSelectChange,
  sharedUsers = [],
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [measuredWidth, setMeasuredWidth] = useState<number>(MOBILE_WIDTH);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Ölçüm: mobilde grid hücresi genişliğine göre ölç
  useLayoutEffect(() => {
    if (isMobile && wrapperRef.current) {
      const w = wrapperRef.current.offsetWidth || MOBILE_WIDTH;
      setMeasuredWidth(w);
    } else {
      setMeasuredWidth(minWidth);
    }
  }, [isMobile, minWidth]);

  const effectiveWidth = isMobile ? measuredWidth : minWidth;
  const effectiveMinHeight = isMobile ? Math.round(effectiveWidth * MOBILE_RATIO) : minHeight;

  const [contentHeight, setContentHeight] = useState(effectiveMinHeight);

  useLayoutEffect(() => {
    if (isMobile) {
      setContentHeight(effectiveMinHeight);
      return;
    }
    if (contentRef.current) {
      const h = contentRef.current.offsetHeight - 20;
      setContentHeight(h < effectiveMinHeight ? effectiveMinHeight : h);
    } else {
      setContentHeight(effectiveMinHeight);
    }
  }, [children, effectiveMinHeight, isMobile]);

  return (
    <div
      ref={wrapperRef}
      className={`${styles.wrapper} ${className || ""}`}
      style={{ ...style, width: isMobile ? '100%' as any : minWidth, height: contentHeight, cursor: onClick ? 'pointer' : undefined }}
      onClick={onClick}
    >
      {selectable && (
        <Checkbox
          checked={selected}
          onChange={e => {
            onSelectChange?.(e.target.checked);
          }}
          className={styles.selectCheckbox}
          style={{ position: "absolute", top: folderType === 'file' ?40 : 80, left: 12, zIndex: 10 }}
          onClick={e => e.stopPropagation()}
        />
      )}
      {folderType === 'folder' ? (
        <FolderSvg width={effectiveWidth} height={contentHeight} />
      ) : (
        <div className={styles.fileIcon}>
          <div className={styles.fileBorder} />
        </div>
      )}
      <div
        className={styles.contentWrapper}
        style={{ width: effectiveWidth, height: contentHeight, position: "absolute", top: 0, left: 0 }}
      >
        <div ref={contentRef} className={folderType === 'folder' ? styles.folderContent : styles.fileContent}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default FolderCard; 